// =============================================================================
// RESUME TEMPLATES — the template META registry (docs/design/
// resume-template-library-2026-07-05.md §3 "data-in-DB split", Decision 2/13).
//
// This table is the DATA half of a template: id, family key, tier, locale
// applicability, page size, picker ordering, which claim_keys justify its
// design. It is NOT the layout — layout (the renderer code: React screen
// component, react-pdf document, docx style builder, HTML string builder) is
// CODE, keyed by the SAME `family` string, living in
// apps/throughline/src/lib/resumeTemplates/registry.ts. A DB row can never
// point at a renderer that doesn't exist — the registry module asserts every
// row here has a matching renderer entry (and vice versa), the same
// "row <-> code mutual check" platform_format/platform_criteria establish for
// their own validated-config shape.
//
// Cross-user (NO userId) — a template's meta (its tier, its locale support,
// its ordering) is one truth for every user, exactly like platform_format /
// field_practices. Additive only (rule #11): CREATE TABLE IF NOT EXISTS via
// the Neon tool, mirrored here, SQL file docs/design/templates-p2-neon.sql
// (never applied by this code).
//
// SEEDING: this Phase-2 lane ships 4 families END-TO-END (ats-plain,
// counter-ready, recent-graduate, modern-professional) and registers the
// other 10 as `status:'coming'` STUBS (a real row, honestly marked, never a
// rendered fake) — see resume_templates_registry.ts's RESUME_TEMPLATE_META
// for which families have real renderer code today (status:'built'). Seeding
// all 14 rows (this table) is independent of which families are BUILT (the
// renderer registry) — exactly the "meta = data, renderer = code" split the
// design calls for. The row shape here MIRRORS ResumeTemplateMeta exactly
// (resume_templates_registry.ts) — that file is the single source of truth
// for the vocabulary (family/tier/status/structure); this table imports its
// TYPES only (erased at compile time) so a server-side seed script can
// insert `RESUME_TEMPLATE_META` directly with zero re-typing.
// =============================================================================

import { boolean, index, integer, jsonb, pgTable, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';
import type { ResumeFormatVariant } from './resume_document';
import type {
  ResumeTemplateFamily,
  ResumeTemplateStatus,
  ResumeTemplateStructure,
  ResumeTemplateTier,
} from './resume_templates_registry';

export type {
  ResumeTemplateFamily,
  ResumeTemplateStatus,
  ResumeTemplateStructure,
  ResumeTemplateTier,
} from './resume_templates_registry';

export const resumeTemplates = pgTable(
  'resume_templates',
  {
    id: id(),
    // Stable dedup/lookup key — the SAME string the renderer registry keys
    // its code on (registry.ts's REGISTERED_FAMILIES/STUB_FAMILIES). UNIQUE
    // so a re-seed upserts rather than duplicating.
    family: varchar('family', { length: 40 }).$type<ResumeTemplateFamily>().notNull().unique(),
    displayName: varchar('display_name', { length: 80 }).notNull(),
    // One-line "who it's for" — the picker's card subtitle. Mira's own words,
    // never AI-tell marketing copy.
    forWhom: varchar('for_whom', { length: 240 }).notNull(),
    tier: varchar('tier', { length: 16 }).$type<ResumeTemplateTier>().notNull().default('premium'),
    status: varchar('status', { length: 16 }).$type<ResumeTemplateStatus>().notNull().default('coming'),
    structure: varchar('structure', { length: 16 })
      .$type<ResumeTemplateStructure>()
      .notNull()
      .default('single-column'),
    // Which locale variants this family renders correctly under (§9) — an
    // array so a family can support several (e.g. ['us_standard','gb_standard',
    // 'au_standard']); selection-criteria supports only ['au_selection_criteria'].
    // Never empty — the seed/insert path should always name at least one.
    supportedLocales: jsonb('supported_locales').$type<ResumeFormatVariant[]>().notNull(),
    // Whether this family accepts the user's chosen accent colour (§10) — false
    // ONLY for ats-plain (monochrome-by-design, the one deliberate exception).
    acceptsAccent: boolean('accepts_accent').notNull().default(true),
    // Picker display order (ascending). Ties broken by family string, so the
    // order is always deterministic even before every row has a distinct value.
    sortOrder: integer('sort_order').notNull().default(0),
    // The claim_keys (resume_template_sources.claim_key) that justify this
    // family's design choices — the picker's "why this works" panel is the
    // joined, validated, cited claims for exactly these keys. No citation, no
    // claim (§5) — a claim_key with no VALIDATED source row simply doesn't
    // render, this array is never itself the gate.
    sourceClaimKeys: jsonb('source_claim_keys').$type<string[]>().notNull().default([]),
    ...timestamps,
  },
  (t) => ({
    familyIdx: index('resume_templates_family_idx').on(t.family),
    tierIdx: index('resume_templates_tier_idx').on(t.tier),
    statusIdx: index('resume_templates_status_idx').on(t.status),
    sortIdx: index('resume_templates_sort_idx').on(t.sortOrder),
  }),
);

// =============================================================================
// RESUME TEMPLATE SOURCES — the verified-source lake (§5), the SECOND
// materialized NO-INVENTION pillar (the first being the per-person grounding
// gate). Follows field_practices/platform_criteria exactly: cross-user,
// citation-or-reject at the write boundary, validation-gated at the read
// boundary, staleness-tracked.
// =============================================================================

/** The validation gate states — mirrors platform_criteria's
 *  CriterionValidationStatus exactly (same closed vocabulary, same meaning):
 *  only 'validated' rows are ever read into product copy. */
export type TemplateSourceValidationStatus = 'validated' | 'flagged' | 'contested' | 'rejected';

/** The category a claim belongs to — drives which family/locale/section of
 *  copy it can justify. Free-text-shaped varchar (house convention), typed
 *  here for the closed set this Phase actually seeds. */
export type TemplateSourceCategory =
  | 'ats'
  | 'locale-us'
  | 'locale-au'
  | 'locale-uk'
  | 'selection-criteria'
  | 'fidelity';

export const resumeTemplateSources = pgTable(
  'resume_template_sources',
  {
    id: id(),
    // e.g. 'ats.single_column', 'ats.no_tables' — UNIQUE, the dedup/lookup key
    // every resumeTemplates.sourceClaimKeys entry and every read call
    // (TemplateSourcesService.copyFor(claimKeys)) resolves against.
    claimKey: varchar('claim_key', { length: 120 }).notNull().unique(),
    // The human-readable claim, e.g. "ATS parsers read a document as a
    // continuous, linear stream of text."
    claim: varchar('claim', { length: 500 }).notNull(),
    category: varchar('category', { length: 24 }).$type<TemplateSourceCategory>().notNull(),
    sourceName: varchar('source_name', { length: 200 }).notNull(),
    // REQUIRED — citation-or-reject (NOT NULL at the column level AND the
    // write-path guard in TemplateSourcesService rejects an empty string;
    // mirrors field_practices' sourceRef-required + platform_criteria's
    // "physically cannot enter a score" posture).
    sourceUrl: varchar('source_url', { length: 500 }).notNull(),
    // Verbatim quote supporting the claim — not a paraphrase, so a future
    // audit can diff the excerpt against the live source text.
    excerpt: varchar('excerpt', { length: 1000 }).notNull(),
    retrievedAt: varchar('retrieved_at', { length: 32 }).notNull(),
    validationStatus: varchar('validation_status', { length: 16 })
      .$type<TemplateSourceValidationStatus>()
      .notNull()
      .default('flagged'),
    // Conventions drift — a past-due row still SERVES (with stale:true, the
    // read helper's job), it just enters a re-verify worklist rather than
    // being silently trusted forever. Null = no staleness policy set.
    staleAfter: varchar('stale_after', { length: 32 }),
    ...timestamps,
  },
  (t) => ({
    claimKeyIdx: index('resume_template_sources_claim_key_idx').on(t.claimKey),
    categoryIdx: index('resume_template_sources_category_idx').on(t.category),
    statusIdx: index('resume_template_sources_status_idx').on(t.validationStatus),
    // Dedup: one row per claim_key (already unique above) — kept as an
    // explicit named index too so the "re-seed upserts" intent is documented
    // at the index layer, mirroring field_practices' own belt-and-suspenders
    // uniqueIndex-plus-index style.
    claimKeyUq: uniqueIndex('resume_template_sources_claim_key_uq').on(t.claimKey),
  }),
);
